import {
  useEffect,
  useState,
} from 'react';
import {
  Callout,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  useLoad,
  rowOpen,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../../client.ts';
import { Failure, DeleteIcon, IconButton, STANDARD_BRAND_HEX } from '../../shared.tsx';
import { BrandDetailModal } from '../../brands/BrandDetailModal.tsx';
import { SetBrandColourModal } from '../../brands/SetBrandColourModal.tsx';
import { useAbilities } from '../../abilities.tsx';
import { brandStanding } from '../../entity-actions.ts';
import { LifecycleDialog } from '../../LifecycleDialog.tsx';
import { t } from '../../../messages.ts';

/** Same click-to-load shape as `OperatorAffiliatesPanel` — §3.9c logs per access. */
export function OperatorBrandsPanel({ api, operatorId, operatorState, shown, onShow }: {
  api: PlatformApi;
  operatorId: string;
  /** The operator's lifecycle state, handed to each brand's popup. */
  operatorState: string;
  shown: boolean;
  onShow: () => void;
}) {
  const { can } = useAbilities();
  const [version, setVersion] = useState(0);
  // Batch 4 item 3 — deleting a brand lives here, not in the operator's own
  // console, which keeps activate/deactivate and nothing destructive. Restore
  // and purge are in the brand's own popup, where its standing is shown.
  const [deleting, setDeleting] = useState<{ brand_id: string; display_name: string } | null>(null);
  /** Which brand's own popup is open. */
  const [open, setOpen] = useState<string | null>(null);
  // Re-reading this list is a logged access, and while it re-reads the panel
  // shows a placeholder that would pull the open popup out from under whoever
  // is in it — so a change made in the popup refreshes the list on the way out.
  const [dirty, setDirty] = useState(false);
  /** §12.4 — which brand's "Set colour" dialog is open. */
  const [settingColour, setSettingColour] = useState<{ brand_id: string; display_name: string; theme_accent: string | null } | null>(null);
  // §12.4 — the response from `setBrandTheme` echoed straight into the row,
  // by brand id, rather than a full reload: the reload would hit this panel's
  // own click-to-load read again, which is a logged cross-tenant access
  // (§3.9c) a colour swap has no reason to cause a second one of.
  const [colourOverride, setColourOverride] = useState<Record<string, string | null>>({});
  // See OperatorAffiliatesPanel — the guard has to be in the loader.
  const loaded = useLoad(
    () => (shown ? api.operatorBrands(operatorId, true) : Promise.resolve(null)),
    [operatorId, shown, version],
  );
  useEffect(() => { setColourOverride({}); }, [version]);

  if (!shown) {
    return (
      <Panel title={t('brand.panel.title')}>
        <Callout>
          {t('brand.panel.crossTenant')}
        </Callout>
        <div className="form-actions">
          <button className="primary" onClick={onShow}>{t('brand.panel.view')}</button>
        </div>
      </Panel>
    );
  }

  if (loaded.state === 'error') return <Panel title={t('brand.panel.title')}><Failure error={loaded.error} /></Panel>;
  if (loaded.state === 'loading' || !loaded.value) {
    return <Panel title={t('brand.panel.title')}><Loading rows={2} /></Panel>;
  }

  const openBrand = loaded.value.items.find((b) => b.brand_id === open);

  return (
    <Panel
      title={t('brand.panel.titleFor', { operator: loaded.value.operator })}
      aside={<span className="count count-badge">{loaded.value.items.length}</span>}
    >
      {loaded.value.items.length === 0 ? (
        <Empty>{t('brand.panel.empty')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <th>{t('brand.panel.colBrand')}</th><th>{t('brand.panel.colCode')}</th><th>{t('brand.panel.colCurrency')}</th><th>{t('brand.panel.colDomain')}</th>
                <th>{t('brand.panel.colStatus')}</th><th>{t('brand.panel.colColour')}</th><th>{t('brand.panel.colProducts')}</th><th className="col-action" />
              </tr>
            </thead>
            <tbody>
              {loaded.value.items.map((b) => {
                const openThis = () => setOpen(b.brand_id);
                // §12.4 — the swatch reflects a colour just set here even
                // before the next reload confirms it server-side.
                const theme = b.brand_id in colourOverride ? colourOverride[b.brand_id]! : b.theme_accent;
                const standing = brandStanding(b);
                const gone = standing.deleted || standing.purged;
                return (
                /* Batch 4 item 3 — a deactivated brand's row reads differently
                   from an active one at a glance, rather than only in the
                   Status cell. */
                <tr
                  key={b.brand_id}
                  className={b.status === 'ACTIVE' && !gone ? 'row-clickable' : 'row-inactive row-clickable'}
                  onClick={openThis}
                  {...rowOpen(openThis)}
                >
                  <td>{b.display_name}</td>
                  <td className="faint">{b.code}</td>
                  <td className="num faint">{b.base_currency}</td>
                  <td className="faint">{b.tracking_domain}</td>
                  <td>
                    {/* Batch 2 item 6 — inactive is neutral, not overdue;
                        this brand is off, not failed or blocked. */}
                    {standing.purged ? <Chip tone="overdue">{t('ent.state.purged')}</Chip>
                      : standing.deleted ? <Chip tone="neutral">{t('ent.state.deleted')}</Chip>
                        : <Chip tone={b.status === 'ACTIVE' ? 'locked' : 'neutral'}>{b.status}</Chip>}
                  </td>
                  <td>
                    {/* §12.4 — a colour is not a commercial term §3.9d gates,
                        so this is a direct write, not a proposal. */}
                    <span className="row" style={{ flexWrap: 'nowrap' }}>
                      <span
                        aria-hidden="true"
                        style={{
                          display: 'inline-block', flex: 'none', width: '14px', height: '14px',
                          borderRadius: '50%', border: '1px solid var(--line-strong)',
                          background: theme ?? STANDARD_BRAND_HEX,
                        }}
                      />
                      <span className="faint mono">{theme ?? t('brand.panel.standard')}</span>
                    </span>
                    {can.edit && !gone ? (
                      <div>
                        <button
                          type="button"
                          className="link-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSettingColour({ brand_id: b.brand_id, display_name: b.display_name, theme_accent: theme });
                          }}
                        >
                          {t('brand.panel.setColour')}
                        </button>
                      </div>
                    ) : null}
                  </td>
                  <td>{b.products.length === 0 ? <span className="faint">—</span> : b.products.join(', ')}</td>
                  {/* Pinned right: this table is eight columns wide inside a
                      popup, and the delete was the column that scrolled off
                      the edge. `IconButton` stops the press reaching the row
                      underneath it. */}
                  <td className="num col-action">
                    {can.remove && !gone ? (
                      <IconButton
                        label={t('brand.panel.deleteLabel', { name: b.display_name })}
                        danger
                        onClick={() => {
                          setDeleting({ brand_id: b.brand_id, display_name: b.display_name });
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    ) : null}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      )}
      <Callout>{loaded.value.note}</Callout>

      {openBrand ? (
        <BrandDetailModal
          api={api}
          operatorId={operatorId}
          brand={{ ...openBrand, operator_state: operatorState }}
          onChanged={() => setDirty(true)}
          onClose={() => {
            setOpen(null);
            if (dirty) {
              setDirty(false);
              setVersion((v) => v + 1);
            }
          }}
        />
      ) : null}

      {settingColour ? (
        <SetBrandColourModal
          api={api}
          operatorId={operatorId}
          brand={settingColour}
          onClose={() => setSettingColour(null)}
          onSaved={(r) => {
            setColourOverride((c) => ({ ...c, [r.brand_id]: r.theme_accent }));
            setSettingColour(null);
          }}
        />
      ) : null}

      {deleting ? (
        <LifecycleDialog
          mode="delete"
          kind="brand"
          name={deleting.display_name}
          needsName
          run={(body) => api.brandDeletion(operatorId, deleting.brand_id, 'delete', body)}
          onClose={() => setDeleting(null)}
          onDone={() => { setDeleting(null); setVersion((v) => v + 1); }}
        />
      ) : null}
    </Panel>
  );
}
